import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  Pencil,
  Trash2,
  Plus,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import PageHeader from "../../components/shared/PageHeader";
import {
  listAllPreQuoteBrands,
  listAllPreQuoteSets,
  createPQCBrand,
  updatePQCBrand,
  deletePQCBrand,
  reorderPQCBrands,
  createPQCSet,
  updatePQCSet,
  deletePQCSet,
  reorderPQCSets,
} from "../../reducers/prequotedcooks.slice";
import { AuthContext } from "../../context/authContext";
import useMediaQuery from "../../components/shared/useMediaQuery";

const getSortableId = (type, id) => `${type}:${id}`;

const buildOrderPayload = (items) =>
  items.map((item, index) => ({ id: item.id, display_order: index + 1 }));

function SortableItemRow({ item, type, index, active, dense = false, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: getSortableId(type, item.id) });

  return (
    <div
      ref={setNodeRef}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: dense ? "8px 10px 8px 6px" : "10px 12px 10px 8px",
        background: active ? "#1e1e1e" : "transparent",
        borderRadius: dense ? 6 : 8,
        border: isDragging ? "1px solid rgba(0,173,181,0.6)" : "1px solid transparent",
        boxShadow: isDragging ? "0 10px 24px rgba(0,0,0,0.28)" : "none",
        opacity: isDragging ? 0.58 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        position: "relative",
        zIndex: isDragging ? 2 : "auto",
      }}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Move ${item.name}`}
        title="Drag to reorder"
        style={{
          width: dense ? 24 : 26,
          height: dense ? 30 : 32,
          border: "none",
          background: "transparent",
          color: "#8b9296",
          cursor: isDragging ? "grabbing" : "grab",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          touchAction: "none",
        }}
      >
        <GripVertical size={dense ? 15 : 16} />
      </button>
      <span
        aria-hidden="true"
        style={{
          width: dense ? 24 : 26,
          height: dense ? 24 : 26,
          borderRadius: 999,
          background: dense ? "rgba(255,255,255,0.08)" : "rgba(0,173,181,0.12)",
          color: dense ? "#c3c8cc" : "#80e5e9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: dense ? 11.5 : 12,
          fontWeight: 700,
          flex: "0 0 auto",
        }}
      >
        {index + 1}
      </span>
      {children}
    </div>
  );
}

export default function BrandsAndSetsPage() {
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [brands, setBrands] = useState([]);
  const [expandedBrandId, setExpandedBrandId] = useState(null);
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [setsLoading, setSetsLoading] = useState(false);

  const [newBrandName, setNewBrandName] = useState("");
  const [newSetName, setNewSetName] = useState("");

  const [editItem, setEditItem] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);
  const [reorderMessage, setReorderMessage] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const brandSortableIds = useMemo(
    () => brands.map((brand) => getSortableId("brand", brand.id)),
    [brands]
  );
  const setSortableIds = useMemo(
    () => sets.map((set) => getSortableId("set", set.id)),
    [sets]
  );

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const res = await dispatch(listAllPreQuoteBrands()).unwrap();
      setBrands(res || []);
    } catch (err) {
      showToast(err?.message || "Failed to load brands", "error");
    }
    setLoading(false);
  };

  const fetchSets = async (brandId) => {
    setSetsLoading(true);
    try {
      const res = await dispatch(listAllPreQuoteSets({ brand_id: brandId })).unwrap();
      setSets(res || []);
    } catch (err) {
      showToast(err?.message || "Failed to load sets", "error");
    }
    setSetsLoading(false);
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    if (expandedBrandId) {
      fetchSets(expandedBrandId);
    } else {
      setSets([]);
    }
  }, [expandedBrandId]);

  const handleCreateBrand = async () => {
    if (!newBrandName.trim()) return;
    try {
      await dispatch(createPQCBrand({ name: newBrandName.trim() })).unwrap();
      showToast("Brand created", "success");
      setNewBrandName("");
      fetchBrands();
    } catch (err) {
      showToast(err?.message || "Failed to create brand", "error");
    }
  };

  const handleCreateSet = async () => {
    if (!newSetName.trim() || !expandedBrandId) return;
    try {
      await dispatch(createPQCSet({ brand_id: expandedBrandId, name: newSetName.trim() })).unwrap();
      showToast("Set created", "success");
      setNewSetName("");
      fetchSets(expandedBrandId);
    } catch (err) {
      showToast(err?.message || "Failed to create set", "error");
    }
  };

  const handleEdit = (item, type) => {
    setEditItem({ ...item, type });
    setEditName(item.name);
  };

  const handleEditSave = async () => {
    if (!editName.trim() || !editItem) return;
    try {
      if (editItem.type === "brand") {
        await dispatch(updatePQCBrand({ id: editItem.id, name: editName.trim() })).unwrap();
        showToast("Brand renamed", "success");
        fetchBrands();
      } else {
        await dispatch(updatePQCSet({ id: editItem.id, name: editName.trim() })).unwrap();
        showToast("Set renamed", "success");
        fetchSets(expandedBrandId);
      }
      setEditItem(null);
      setEditName("");
    } catch (err) {
      showToast(err?.message || "Failed to update", "error");
    }
  };

  const handleDelete = (item, type) => {
    setDeleteItem({ ...item, type });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItem) return;
    try {
      if (deleteItem.type === "brand") {
        await dispatch(deletePQCBrand({ id: deleteItem.id })).unwrap();
        showToast("Brand deleted", "success");
        if (expandedBrandId === deleteItem.id) {
          setExpandedBrandId(null);
        }
        fetchBrands();
      } else {
        await dispatch(deletePQCSet({ id: deleteItem.id })).unwrap();
        showToast("Set deleted", "success");
        fetchSets(expandedBrandId);
      }
      setDeleteItem(null);
    } catch (err) {
      showToast(err?.message || "Failed to delete", "error");
    }
  };

  const persistBrandOrder = async (updated) => {
    try {
      await dispatch(reorderPQCBrands({ brands: buildOrderPayload(updated) })).unwrap();
    } catch (err) {
      showToast(err?.message || "Failed to reorder brands", "error");
      fetchBrands();
    }
  };

  const persistSetOrder = async (updated) => {
    try {
      await dispatch(reorderPQCSets({ sets: buildOrderPayload(updated) })).unwrap();
    } catch (err) {
      showToast(err?.message || "Failed to reorder sets", "error");
      fetchSets(expandedBrandId);
    }
  };

  const applyReorder = (type, fromIndex, toIndex) => {
    const items = type === "brand" ? brands : sets;
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || toIndex >= items.length) return;

    const updated = arrayMove(items, fromIndex, toIndex);
    const movedItem = updated[toIndex];

    if (type === "brand") {
      setBrands(updated);
      persistBrandOrder(updated);
    } else {
      setSets(updated);
      persistSetOrder(updated);
    }

    setReorderMessage(`${movedItem.name} moved to preference ${toIndex + 1}`);
  };

  const handleDragEnd = (type, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    if (!activeId.startsWith(`${type}:`) || !overId.startsWith(`${type}:`)) return;

    const items = type === "brand" ? brands : sets;
    const fromIndex = items.findIndex((item) => getSortableId(type, item.id) === activeId);
    const toIndex = items.findIndex((item) => getSortableId(type, item.id) === overId);

    applyReorder(type, fromIndex, toIndex);
  };

  const toggleExpand = (brandId) => {
    setExpandedBrandId(expandedBrandId === brandId ? null : brandId);
    setNewSetName("");
  };

  return (
    <div style={{ padding: isMobile ? "16px 12px" : "24px 32px", maxWidth: 900 }}>
      <PageHeader
        title="Brands & Sets"
        subtitle="Manage catalog brands and sets ordering"
      />

      <div style={{ marginTop: 28 }}>
        <Label>Add New Brand</Label>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Input
            placeholder="Brand name"
            value={newBrandName}
            onChange={(e) => setNewBrandName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreateBrand()}
            style={{ maxWidth: 340 }}
          />
          <Button
            onClick={handleCreateBrand}
            disabled={!newBrandName.trim()}
            size="sm"
          >
            <Plus size={14} />
            Add
          </Button>
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        {loading ? (
          <p style={{ color: "#c3c8cc", fontSize: 14 }}>Loading brands...</p>
        ) : brands.length === 0 ? (
          <p style={{ color: "#c3c8cc", fontSize: 14 }}>No brands found. Create one above.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
              aria-live="polite"
            >
              {reorderMessage}
            </span>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => handleDragEnd("brand", event)}
            >
              <SortableContext items={brandSortableIds} strategy={verticalListSortingStrategy}>
                {brands.map((brand, index) => (
                  <div key={brand.id}>
                    <SortableItemRow
                      item={brand}
                      type="brand"
                      index={index}
                      active={expandedBrandId === brand.id}
                    >
                      <button
                        onClick={() => toggleExpand(brand.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#c3c8cc",
                          cursor: "pointer",
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {expandedBrandId === brand.id ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </button>

                      <span
                        style={{
                          flex: 1,
                          color: "#fff",
                          fontSize: 14,
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                        onClick={() => toggleExpand(brand.id)}
                      >
                        {brand.name}
                      </span>

                      {brand.product_count != null && (
                        <span style={{ color: "#8b9296", fontSize: 12 }}>
                          {brand.product_count} product{brand.product_count !== 1 ? "s" : ""}
                        </span>
                      )}

                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleEdit(brand, "brand")}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleDelete(brand, "brand")}
                        style={{ color: "#f27059" }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </SortableItemRow>

                    {expandedBrandId === brand.id && (
                      <div
                        style={{
                          marginLeft: isMobile ? 16 : 32,
                          padding: "8px 0 12px",
                          borderLeft: "2px solid rgba(255,255,255,0.06)",
                          paddingLeft: 16,
                        }}
                      >
                        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                          <Input
                            placeholder="Set name"
                            value={newSetName}
                            onChange={(e) => setNewSetName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleCreateSet()}
                            style={{ maxWidth: 280 }}
                          />
                          <Button
                            onClick={handleCreateSet}
                            disabled={!newSetName.trim()}
                            size="sm"
                          >
                            <Plus size={14} />
                            Add Set
                          </Button>
                        </div>

                        {setsLoading ? (
                          <p style={{ color: "#c3c8cc", fontSize: 13 }}>Loading sets...</p>
                        ) : sets.length === 0 ? (
                          <p style={{ color: "#c3c8cc", fontSize: 13 }}>No sets in this brand.</p>
                        ) : (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) => handleDragEnd("set", event)}
                          >
                            <SortableContext items={setSortableIds} strategy={verticalListSortingStrategy}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                {sets.map((set, sIndex) => (
                                  <SortableItemRow
                                    key={set.id}
                                    item={set}
                                    type="set"
                                    index={sIndex}
                                    dense
                                  >
                                    <span style={{ flex: 1, color: "#fff", fontSize: 13.5 }}>
                                      {set.name}
                                    </span>

                                    {set.product_count != null && (
                                      <span style={{ color: "#8b9296", fontSize: 12 }}>
                                        {set.product_count} product{set.product_count !== 1 ? "s" : ""}
                                      </span>
                                    )}

                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      onClick={() => handleEdit(set, "set")}
                                    >
                                      <Pencil size={14} />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      onClick={() => handleDelete(set, "set")}
                                      style={{ color: "#f27059" }}
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </SortableItemRow>
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>

      <Dialog open={!!editItem} onOpenChange={(open) => { if (!open) setEditItem(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename {editItem?.type === "brand" ? "Brand" : "Set"}</DialogTitle>
            <DialogDescription>
              Enter a new name for this {editItem?.type === "brand" ? "brand" : "set"}.
            </DialogDescription>
          </DialogHeader>
          <div style={{ marginTop: 12 }}>
            <Label>Name</Label>
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleEditSave()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} disabled={!editName.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteItem} onOpenChange={(open) => { if (!open) setDeleteItem(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteItem?.type === "brand" ? "Brand" : "Set"}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteItem?.name}"?
              {deleteItem?.type === "brand" && " This will also hide all sets under this brand."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteItem(null)} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
